import { useContext, useEffect, useState } from 'react';
import {
  Tab,
  TabListProps,
  TabPanelProps,
  TabPanelsProps,
  TabProps,
  TabsProps,
} from '@chakra-ui/react';
import { UsePropertyTabsConfig } from './types';
import Context from './context';

export default function usePropertyTabs({
  sectionRef,
  prependTabs = [],
  appendTabs = [],
}: UsePropertyTabsConfig = {}) {
  const context = useContext(Context);
  const [additionalTabIndex, setAdditionalTabIndex] = useState<
    number | undefined
  >(undefined);

  // if someone else (another hook instance) changes the global index
  // then we follow that call and clear the additionalIndex if we have it set
  useEffect(() => {
    setAdditionalTabIndex(undefined);
  }, [context?.tabIndex]);

  if (!context) {
    throw new Error(
      "No context found, seems you didn't wrap your component in a PropertyTabsProvider",
    );
  }

  const {
    tabIndex: globalTabIndex,
    setTabIndex: setGlobalTabIndex,
    tabCount,
    properties,
  } = context;

  // start and end indices for the global tabs in our (this hook call's) case
  const globalTabsStartIndex = prependTabs.length;
  const globalTabsEndIndex = prependTabs.length + tabCount - 1;

  // calculate our actual tab index:
  // if we have set an additional tab index, use that
  // otherwise use the global index while accounting for any prepended tabs
  const tabIndex =
    additionalTabIndex !== undefined
      ? additionalTabIndex
      : globalTabsStartIndex + globalTabIndex;

  const setTabIndex = (index: number) => {
    if (sectionRef?.current) {
      sectionRef.current.scrollIntoView();
    }
    // if we clicked a tab whose index is less than or greater than the global tabs indices,
    // then we leave the global index as it is and set the additional index
    if (index < globalTabsStartIndex || index > globalTabsEndIndex) {
      setAdditionalTabIndex(index);
    } else {
      // clear the additional index if a global tab was clicked
      setAdditionalTabIndex(undefined);
      // and set the global tab index while accounting for any prepended tabs
      setGlobalTabIndex(index - globalTabsStartIndex);
    }
  };

  const tabsProps: Omit<TabsProps, 'children'> = {
    variant: 'unstyled',
    index: tabIndex,
    onChange: (index: number) => setTabIndex(index),
  };

  const tabProps: Omit<TabProps, 'children'> = {
    fontWeight: '600',
    px: [0],
    pb: 0,
    mb: [4, null, 1],
    mr: [3, null, 0],
    _active: { border: 0 },
    _focus: { outline: 0 },
    _selected: { borderBottom: '2px solid', color: 'primary.500' },
    _hover: { color: 'primary.500' },
  };

  const tabListProps: TabListProps = {
    border: 0,
    flexDirection: [null, null, 'column'],
    alignItems: 'flex-start',
    position: [null, null, 'sticky'],
    top: [null, null, 10],
    children: [
      ...prependTabs.map((tab) => (
        <Tab {...tabProps} key={tab.key}>
          {tab.label}
        </Tab>
      )),
      ...properties.map((property) => (
        <Tab {...tabProps} key={property.id}>
          {property.property_name}
        </Tab>
      )),
      ...appendTabs.map((tab) => (
        <Tab {...tabProps} key={tab.key}>
          {tab.label}
        </Tab>
      )),
    ],
  };

  const tabPanelsProps: Omit<TabPanelsProps, 'children'> = {
    p: 0,
  };

  const tabPanelProps: Omit<TabPanelProps, 'children'> = {
    p: 0,
  };

  return {
    properties,
    tabIndex,
    setTabIndex,
    tabsProps,
    tabListProps,
    tabProps,
    tabPanelsProps,
    tabPanelProps,
  };
}

export type UsePropertyTabsProps = ReturnType<typeof usePropertyTabs>;
